import { forwardRef, useImperativeHandle, useRef, useMemo } from "react";
import { DesignElement } from "./types";
import { Heading } from "../ui/Heading";
import { Input } from "../ui/Input";
import BackgroundLibrary from "./BackgroundLibrary";
import FontSelector, { FontOption } from "./FontSelector";
import ShapeLibrary from "./ShapeLibrary";
import { useStore } from "../../context/StoreContext";

export interface ElementPropertiesPanelRef {
  focusTextInput: () => void;
}

interface ElementPropertiesPanelProps {
  element?: DesignElement;
  isCanvasSelected?: boolean;
  onUpdate?: (updates: Partial<DesignElement>) => void;
  onDelete?: () => void;
  onSelectBackground?: (url: string) => void;
  selectedBackground?: string;
  backgroundColor?: string;
  onBackgroundColorChange?: (color: string) => void;
  canvasSize?: string;
  backgroundImages?: string[];
  availableShapes?: Array<{
    id: string;
    name: string;
    url: string;
    category?: string;
  }>;
  // 3D-specific props
  is3D?: boolean;
  backgroundDepth?: number;
  onBackgroundDepthChange?: (depth: number) => void;
  backgroundForm?: "circular" | "rectangular" | "heart" | "auto";
  onBackgroundFormChange?: (
    form: "circular" | "rectangular" | "heart" | "auto"
  ) => void;
  backgroundEnabled?: boolean;
  onBackgroundEnabledChange?: (enabled: boolean) => void;
  backgroundWidth?: number;
  onBackgroundWidthChange?: (width: number) => void;
  backgroundHeight?: number;
  onBackgroundHeightChange?: (height: number) => void;
  backgroundBorderRadius?: number;
  onBackgroundBorderRadiusChange?: (radius: number) => void;
  keychainRingEnabled?: boolean;
  onKeychainRingEnabledChange?: (enabled: boolean) => void;
  keychainRingSide?: "top" | "bottom" | "left" | "right";
  onKeychainRingSideChange?: (
    side: "top" | "bottom" | "left" | "right"
  ) => void;
  keychainRingEmbeddingDepth?: number;
  onKeychainRingEmbeddingDepthChange?: (depth: number) => void;
  keychainRingSize?: number;
  onKeychainRingSizeChange?: (size: number) => void;
  keychainRingThickness?: number;
  onKeychainRingThicknessChange?: (thickness: number) => void;
  availableColors?: string[]; // Available colors for 3D products (when provided, shows swatches instead of full picker)
  allowedDesignerFonts?: string[]; // Allowed fonts for this product (if empty/null, uses store-level or all fonts)
}

const ElementPropertiesPanel = forwardRef<
  ElementPropertiesPanelRef,
  ElementPropertiesPanelProps
>(
  (
    {
      element,
      isCanvasSelected = false,
      onUpdate,
      onDelete: _onDelete,
      onSelectBackground,
      selectedBackground,
      backgroundColor,
      onBackgroundColorChange,
      backgroundImages = [],
      availableShapes = [],
      is3D = false,
      backgroundDepth,
      onBackgroundDepthChange,
      backgroundForm,
      onBackgroundFormChange,
      backgroundEnabled,
      onBackgroundEnabledChange,
      backgroundWidth,
      onBackgroundWidthChange,
      backgroundHeight,
      onBackgroundHeightChange,
      backgroundBorderRadius,
      onBackgroundBorderRadiusChange,
      keychainRingEnabled,
      onKeychainRingEnabledChange,
      keychainRingSide,
      onKeychainRingSideChange,
      keychainRingEmbeddingDepth,
      onKeychainRingEmbeddingDepthChange,
      keychainRingSize,
      onKeychainRingSizeChange,
      keychainRingThickness,
      onKeychainRingThicknessChange,
      availableColors,
      allowedDesignerFonts,
    },
    ref
  ) => {
    const { store } = useStore();
    const textInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focusTextInput: () => {
        textInputRef.current?.focus();
      },
    }));
    // 3D supported fonts - Only fonts that have actual typeface JSON files in /public/typeface-fonts/
    // These are the fonts that are confirmed to work without falling back to system fonts
    const supported3DFonts: FontOption[] = [
      { value: "Almarai", label: "Almarai" },
      { value: "Anton", label: "Anton" },
      { value: "Barlow Condensed", label: "Barlow Condensed" },
      { value: "BBH Bartle", label: "BBH Bartle" },
      { value: "Cairo", label: "Cairo" },
      { value: "Caveat", label: "Caveat" },
      { value: "Dancing Script", label: "Dancing Script" },
      { value: "Edu SA Beginner", label: "Edu SA Beginner" },
      { value: "Follow", label: "Follow" },
      { value: "Great Vibes", label: "Great Vibes" },
      { value: "Josefin Sans Thin", label: "Josefin Sans Thin" },
      { value: "Lobster", label: "Lobster" },
      { value: "Lobster Two", label: "Lobster Two" },
      { value: "Noto Sans Symbols", label: "Noto Sans Symbols" },
      { value: "Orbitron", label: "Orbitron" },
      { value: "Pacifico", label: "Pacifico" },
      { value: "Satisfy", label: "Satisfy" },
      { value: "Shadows Into Light", label: "Shadows Into Light" },
      { value: "Smooch Sans Thin", label: "Smooch Sans Thin" },
      { value: "Space Grotesk Light", label: "Space Grotesk Light" },
      { value: "Tajawal", label: "Tajawal" },
    ];
    // Determine which fonts to use: product-level > store-level > all fonts
    const allowedFonts = useMemo(() => {
      // For 3D, use supported fonts only
      if (is3D) {
        return supported3DFonts;
      }

      // For 2D, use product-level fonts if available, otherwise store-level, otherwise all
      if (allowedDesignerFonts && allowedDesignerFonts.length > 0) {
        return allowedDesignerFonts.map((f) => ({ value: f, label: f }));
      }

      if (
        store?.allowedDesignerFonts &&
        store.allowedDesignerFonts.length > 0
      ) {
        return store.allowedDesignerFonts.map((f) => ({ value: f, label: f }));
      }

      // Return undefined to show all fonts
      return undefined;
    }, [allowedDesignerFonts, store?.allowedDesignerFonts, is3D]);

    const handleStyleUpdate = (styleKey: string, value: any) => {
      if (!element || !onUpdate) return;
      onUpdate({
        style: {
          ...element.style,
          [styleKey]: value,
        },
      });
    };

    const handlePositionUpdate = (key: "x" | "y", value: number) => {
      if (!element || !onUpdate) return;
      onUpdate({
        position: {
          ...element.position,
          [key]: value,
        },
      });
    };

    const handleSizeUpdate = (key: "width" | "height", value: number) => {
      if (!element || !onUpdate) return;
      onUpdate({
        size: {
          ...element.size,
          [key]: value,
        },
      });
    };

    const handleContentUpdate = (content: string) => {
      if (!element || !onUpdate) return;
      onUpdate({ content });
    };

    // Helper function to render color picker (swatches or full picker)
    const renderColorPicker = (
      value: string,
      onChange: (color: string) => void,
      label?: string
    ) => {
      const hasAvailableColors = availableColors && availableColors.length > 0;

      if (hasAvailableColors) {
        // Show color swatches
        return (
          <div>
            {label && (
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}
              </label>
            )}
            <div className="flex flex-wrap gap-2">
              {availableColors.map((color, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => onChange(color)}
                  className={`w-10 h-10 rounded border-2 transition-all ${
                    value === color
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-16 h-10 p-0 border border-gray-300 rounded"
                style={{ backgroundColor: value }}
                disabled
              />
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#000000"
                className="flex-1"
                readOnly
              />
            </div>
          </div>
        );
      }

      // Show full color picker
      return (
        <div>
          {label && (
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label}
            </label>
          )}
          <div className="flex gap-2">
            <Input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-16 h-10 p-0 border border-gray-300 rounded"
              style={{ backgroundColor: value }}
            />
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#000000"
            />
          </div>
        </div>
      );
    };

    // Canvas Properties
    if (isCanvasSelected) {
      return (
        <div className="p-4 overflow-y-auto">
          <Heading size="sm" className="mb-4">
            Canvas Properties
          </Heading>
          <div className="space-y-4">
            {!is3D && (
              <BackgroundLibrary
                onSelectBackground={onSelectBackground}
                selectedBackground={selectedBackground}
                backgroundImages={backgroundImages}
              />
            )}
            {renderColorPicker(
              backgroundColor || "#FFFFFF",
              (color) => {
                if (onBackgroundColorChange) {
                  onBackgroundColorChange(color);
                }
              },
              "Background Color"
            )}
            {is3D && (
              <>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={backgroundEnabled !== false}
                      onChange={(e) => {
                        if (onBackgroundEnabledChange) {
                          onBackgroundEnabledChange(e.target.checked);
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable Background
                    </span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Depth
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={backgroundDepth || 5}
                    onChange={(e) => {
                      if (onBackgroundDepthChange) {
                        onBackgroundDepthChange(
                          parseInt(e.target.value, 10) || 5
                        );
                      }
                    }}
                    disabled={!backgroundEnabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Form
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    value={backgroundForm || "auto"}
                    onChange={(e) => {
                      if (onBackgroundFormChange) {
                        onBackgroundFormChange(
                          e.target.value as
                            | "circular"
                            | "rectangular"
                            | "heart"
                            | "auto"
                        );
                      }
                    }}
                    disabled={!backgroundEnabled}
                  >
                    <option value="auto">Auto</option>
                    <option value="circular">Circular</option>
                    <option value="rectangular">Rectangular</option>
                    <option value="heart">Heart</option>
                  </select>
                </div>
                {backgroundForm &&
                  backgroundForm !== "auto" &&
                  backgroundEnabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Width
                          </label>
                          <Input
                            type="number"
                            min="10"
                            max="500"
                            value={backgroundWidth || 100}
                            onChange={(e) => {
                              if (onBackgroundWidthChange) {
                                onBackgroundWidthChange(
                                  parseInt(e.target.value, 10) || 100
                                );
                              }
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Height
                          </label>
                          <Input
                            type="number"
                            min="10"
                            max="500"
                            value={backgroundHeight || 60}
                            onChange={(e) => {
                              if (onBackgroundHeightChange) {
                                onBackgroundHeightChange(
                                  parseInt(e.target.value, 10) || 60
                                );
                              }
                            }}
                          />
                        </div>
                      </div>
                      {(backgroundForm === "rectangular" ||
                        backgroundForm === "heart") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Border Radius
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max={Math.min(
                              (backgroundWidth || 100) / 2,
                              (backgroundHeight || 60) / 2
                            )}
                            value={backgroundBorderRadius || 0}
                            onChange={(e) => {
                              if (onBackgroundBorderRadiusChange) {
                                onBackgroundBorderRadiusChange(
                                  parseInt(e.target.value, 10) || 0
                                );
                              }
                            }}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Max:{" "}
                            {Math.min(
                              (backgroundWidth || 100) / 2,
                              (backgroundHeight || 60) / 2
                            ).toFixed(0)}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                {/* Keychain Ring Options */}
                {is3D && (
                  <>
                    <div className="border-t border-gray-200 my-4"></div>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={keychainRingEnabled || false}
                            onChange={(e) => {
                              if (onKeychainRingEnabledChange) {
                                onKeychainRingEnabledChange(e.target.checked);
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Enable Keychain Ring
                          </span>
                        </label>
                      </div>
                      {keychainRingEnabled && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ring Position
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={keychainRingSide || "top"}
                              onChange={(e) => {
                                if (onKeychainRingSideChange) {
                                  onKeychainRingSideChange(
                                    e.target.value as
                                      | "top"
                                      | "bottom"
                                      | "left"
                                      | "right"
                                  );
                                }
                              }}
                            >
                              <option value="top">Top</option>
                              <option value="bottom">Bottom</option>
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ring Embedding Depth (%)
                            </label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={(
                                (keychainRingEmbeddingDepth ?? 0.5) * 100
                              ).toFixed(0)}
                              onChange={(e) => {
                                if (onKeychainRingEmbeddingDepthChange) {
                                  const percentage =
                                    parseFloat(e.target.value) || 0;
                                  const ratio =
                                    Math.max(0, Math.min(100, percentage)) /
                                    100;
                                  onKeychainRingEmbeddingDepthChange(ratio);
                                }
                              }}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Controls how deep the ring is embedded into the
                              model (0% = on edge, 100% = fully embedded)
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ring Size (%)
                            </label>
                            <Input
                              type="number"
                              min="10"
                              max="200"
                              step="1"
                              value={((keychainRingSize ?? 1.0) * 100).toFixed(
                                0
                              )}
                              onChange={(e) => {
                                if (onKeychainRingSizeChange) {
                                  const percentage =
                                    parseFloat(e.target.value) || 100;
                                  const multiplier =
                                    Math.max(10, Math.min(200, percentage)) /
                                    100;
                                  onKeychainRingSizeChange(multiplier);
                                }
                              }}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Controls the size of the ring relative to
                              auto-calculated size (10% = very small, 200% =
                              very large)
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ring Thickness (%)
                            </label>
                            <Input
                              type="number"
                              min="10"
                              max="200"
                              step="1"
                              value={(
                                (keychainRingThickness ?? 1.0) * 100
                              ).toFixed(0)}
                              onChange={(e) => {
                                if (onKeychainRingThicknessChange) {
                                  const percentage =
                                    parseFloat(e.target.value) || 100;
                                  const multiplier =
                                    Math.max(10, Math.min(200, percentage)) /
                                    100;
                                  onKeychainRingThicknessChange(multiplier);
                                }
                              }}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Controls the thickness of the ring (10% = very
                              thin, 200% = very thick)
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    // Element Properties
    if (!element || !onUpdate) {
      return null;
    }

    return (
      <div className="p-4 overflow-y-auto">
        <Heading size="sm" className="mb-4">
          Element Properties
        </Heading>
        <div className="space-y-4">
          {/* Text Element Properties */}
          {element.type === "text" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Content
                </label>
                <Input
                  ref={textInputRef}
                  value={element.content || ""}
                  onChange={(e) => handleContentUpdate(e.target.value)}
                  placeholder="Enter text..."
                />
              </div>

              <div>
                <FontSelector
                  label="Font Family"
                  value={element.style.fontFamily}
                  onChange={(e) =>
                    handleStyleUpdate("fontFamily", e.target.value)
                  }
                  options={allowedFonts}
                />
              </div>

              <div>
                <Input
                  label="Font Size"
                  type="number"
                  min="8"
                  max="200"
                  value={element.style.fontSize || 24}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "fontSize",
                      parseInt(e.target.value, 10) || 24
                    )
                  }
                />
              </div>

              {renderColorPicker(
                element.style.color || "#000000",
                (color) => handleStyleUpdate("color", color),
                "Text Color"
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Weight
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={element.style.fontWeight || "normal"}
                    onChange={(e) =>
                      handleStyleUpdate("fontWeight", e.target.value)
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                    <option value="lighter">Lighter</option>
                    <option value="bolder">Bolder</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Style
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={element.style.fontStyle || "normal"}
                    onChange={(e) =>
                      handleStyleUpdate("fontStyle", e.target.value)
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="italic">Italic</option>
                    <option value="oblique">Oblique</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Align
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={element.style.textAlign || "left"}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "textAlign",
                      e.target.value as "left" | "center" | "right" | "justify"
                    )
                  }
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </div>
              {is3D && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Depth (Thickness)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={element.style.textDepth || 5}
                      onChange={(e) =>
                        handleStyleUpdate(
                          "textDepth",
                          parseInt(e.target.value, 10) || 5
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Orientation
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={element.style.textOrientation || "flat"}
                      onChange={(e) =>
                        handleStyleUpdate(
                          "textOrientation",
                          e.target.value as "flat" | "vertical"
                        )
                      }
                    >
                      <option value="flat">Flat (Laying on back)</option>
                      <option value="vertical">Vertical (Standing)</option>
                    </select>
                  </div>
                </>
              )}
            </>
          )}

          {/* Shape Element Properties */}
          {element.type === "shape" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shape
                </label>
                <ShapeLibrary
                  availableShapes={availableShapes}
                  onSelectShape={(shapeUrl) => {
                    if (onUpdate) {
                      onUpdate({ content: shapeUrl });
                    }
                  }}
                />
              </div>

              {renderColorPicker(
                element.style.backgroundColor || "#000000",
                (color) => handleStyleUpdate("backgroundColor", color),
                "Background Color"
              )}

              {renderColorPicker(
                element.style.borderColor || "#000000",
                (color) => handleStyleUpdate("borderColor", color),
                "Border Color"
              )}

              <div>
                <Input
                  label="Border Width"
                  type="number"
                  min="0"
                  value={element.style.borderWidth || 0}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "borderWidth",
                      parseInt(e.target.value, 10) || 0
                    )
                  }
                />
              </div>

              <div>
                <Input
                  label="Border Radius"
                  type="number"
                  min="0"
                  value={element.style.borderRadius || 0}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "borderRadius",
                      parseInt(e.target.value, 10) || 0
                    )
                  }
                />
              </div>
            </>
          )}

          {/* Frame Element Properties */}
          {element.type === "frame" && (
            <>
              {renderColorPicker(
                element.style.borderColor || "#000000",
                (color) => handleStyleUpdate("borderColor", color),
                "Border Color"
              )}

              <div>
                <Input
                  label="Border Width"
                  type="number"
                  min="0"
                  value={element.style.borderWidth || 2}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "borderWidth",
                      parseInt(e.target.value, 10) || 2
                    )
                  }
                />
              </div>

              <div>
                <Input
                  label="Border Radius"
                  type="number"
                  min="0"
                  value={element.style.borderRadius || 0}
                  onChange={(e) =>
                    handleStyleUpdate(
                      "borderRadius",
                      parseInt(e.target.value, 10) || 0
                    )
                  }
                />
              </div>
            </>
          )}

          {/* Common Properties */}
          <div className="border-t border-gray-200 pt-4">
            <Heading size="sm" className="mb-4">
              Position & Size
            </Heading>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="X Position"
                type="number"
                value={element.position.x}
                onChange={(e) =>
                  handlePositionUpdate("x", parseInt(e.target.value, 10) || 0)
                }
              />
              <Input
                label="Y Position"
                type="number"
                value={element.position.y}
                onChange={(e) =>
                  handlePositionUpdate("y", parseInt(e.target.value, 10) || 0)
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Width"
                type="number"
                min="1"
                value={element.size.width}
                onChange={(e) =>
                  handleSizeUpdate("width", parseInt(e.target.value, 10) || 1)
                }
              />
              <Input
                label="Height"
                type="number"
                min="1"
                value={element.size.height}
                onChange={(e) =>
                  handleSizeUpdate("height", parseInt(e.target.value, 10) || 1)
                }
              />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <Heading size="sm" className="mb-4">
              Transform
            </Heading>
            <Input
              label="Rotation (degrees)"
              type="number"
              min="-360"
              max="360"
              value={element.style.rotation || 0}
              onChange={(e) =>
                handleStyleUpdate("rotation", parseInt(e.target.value, 10) || 0)
              }
            />
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opacity: {((element.style.opacity || 1) * 100).toFixed(0)}%
              </label>
              <Input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={element.style.opacity || 1}
                onChange={(e) =>
                  handleStyleUpdate("opacity", parseFloat(e.target.value) || 1)
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ElementPropertiesPanel.displayName = "ElementPropertiesPanel";

export default ElementPropertiesPanel;
